import { Chip, Divider, toast } from '@montugroup/design-system';
import { alternaleaf, BrandThemeProvider } from '@montugroup/themes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CircularProgress, styled, useMediaQuery, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getAuthData, getRole } from '@/data/service/authService';
import { Tab } from '@/shared-ui/Tabs/Tab';
import { TabPanel } from '@/shared-ui/Tabs/TabPanel';
import { Tabs } from '@/shared-ui/Tabs/Tabs';

import { MedicalCondition } from '@/components/Patient/forms/MedicalCondition/MedicalCondition';

import { MedicalSummary } from '@/components/Patient/forms/MedicalSummary/MedicalSummary';

import { PatientBasicInfo } from '@/components/Patient/forms/PatientBasicInfo/PatientBasicInfo';
import { PatientDetailsIncludeEnum, useGetPatientDetails } from '@/hooks/patients/useGetPatientDetails';
import { useAppStore } from '@/state-management';
import { z } from 'zod';
import { ManageConcession } from '../components/concession';

import { withAmazonConnectCcp } from '@/components/amazon-connect/withAmazonConnectCcp';

import { AcknowledgementModal } from '@/components/Patient/components/AcknowledgementModal/AcknowledgementModal';
import { MedicalInformation } from '@/components/Patient/forms/MedicalInformation/MedicalInformation';
import { usePatientDetailsContent } from '@/components/Patient/hooks/usePatientDetailsContent';
import type { PatientDetailsDataState } from '@/components/Patient/hooks/usePatientDetailsData';
import { usePatientDetailsData } from '@/components/Patient/hooks/usePatientDetailsData';
import { useTabsState } from '@/components/Patient/hooks/useTabsState';
import { PATIENT_TAB_IDS } from '@/components/Patient/Patient.constants';
import PatientDetailsTab from '@/components/Patient/PatientDetailsTab';
import PatientStudiesAlert from '@/components/Patient/PatientStudiesAlert';
import PrescriptionTab from '@/components/Patient/PrescriptionTab';
import ConsultationTab from '@/components/PatientPageV2/ConsultationTab';
import DocumentsTab from '@/components/PatientPageV2/DocumentsTab';
import PrescriptionTabLegacy from '@/components/PatientPageV2/PrescriptionTabLegacy';
import {
  FF_GDC_RELEASE_1,
  FF_PATIENT_DETAILS_SKELETON_ENABLED,
  FF_PATIENT_PAGE_AUTOSAVE_TOUR,
  FF_PRESCRIPTION_TAB_REDESIGN,
  FF_SHOW_PATIENT_STUDY_ALERT,
} from '@/constants/featureFlags';
import { useFeatureFlags } from '@/hooks';
import { useGetPatientAssessments } from '@/hooks/patients/useGetPatientAssessments';
import { useGetPatientConcessionCard } from '@/hooks/patients/useGetPatientConcessionCard';
import type { Consultation } from '@/hooks/patients/usePatientConsultations';
import { usePatientConsultations } from '@/hooks/patients/usePatientConsultations';
import { useUpdatePatientAssessment } from '@/hooks/patients/useUpdatePatientAssessments';
import { HttpStatus } from '@montugroup/apis-styleguide';
import type { UpdatePatientAssessmentBodyDto } from '@montugroup/pms-api-contracts';
import { DateTime } from 'luxon';

import PatientDischargeAndSaveStatus from '@/components/Patient/components/PatientDischargeAndSaveStatus';
import { UnsavedScheduledConsultPrompt } from '@/components/Patient/components/UnsavedScheduledConsultPrompt/UnsavedScheduledConsultPrompt';
import { useIsScheduledConsultActive } from '@/components/Patient/hooks/useIsScheduledConsultActive';
import { usePrescriptionAssistantToolDetails } from '@/components/Patient/hooks/usePrescriptionAssistantToolDetails';
import { PatientContext } from '@/components/Patient/PatientContext';
import { ScheduledConsult } from '@/components/Patient/ScheduledConsult/ScheduledConsult';
import type { PatientTabId } from '@/components/Patient/utils/buildPatientTabConfig';
import { buildPatientTabConfig } from '@/components/Patient/utils/buildPatientTabConfig';
import type { ShowConsultation } from '@/components/PatientPageV2/components/ConsultationTabSections/ShowConsultation';
import { useSaveConsultation } from '@/hooks/consult/useSaveConsultation';
import { useDassAssessment, useDassAssessmentDetails, useDassAssessmentScores } from '@/hooks/dass/useDass';
import { useDoctors } from '@/hooks/doctor/useDoctors';
import { patientProfileAutoSave } from '@/tours';
import { useFeatureNotification } from '@/tours/hooks/useFeatureNotification';
import type { ConsultationSubStatusType } from '@/types';
import type { FormFields as MedicalSummaryFields } from '../components/Patient/forms/MedicalSummary/MedicalSummary.schema';

import { BaseAnalyticsContext as PrescriptionTabAnalyticsContext } from '@/components/Patient/PrescriptionTab/BaseAnalyticsContext';
import { BaseAnalyticsContext as ScheduledConsultAnalyticsContext } from '@/components/Patient/ScheduledConsult/BaseAnalyticsContext/BaseAnalyticsContext';
import { BaseAnalyticsContext as ConsultationTabAnalyticsContext } from '@/components/PatientPageV2/components/BaseAnalyticsContext/BaseAnalyticsContext';

export type OnSaveConsultationOptions = {
  saveConsult?: boolean;
  closeConsultation?: boolean;
  skipAssessment?: boolean;
  askConfirmation?: boolean;
};
const CheckRequiredFieldSchema = z.object({
  // // The following 'preprocess' was added because `isActive` was causing issues in E2E tests. This makes sure value is always a boolean
  isActive: z.preprocess(
    (val) => (val === 'true' ? true : val), // Convert "true" (string) to true (boolean)
    z.literal(true), // Ensure only `true` (boolean) is valid
  ),
  street_line1: z.string().min(1),
  suburb: z.string().min(1),
  postcode: z.string().min(1),
  state: z.coerce.string().min(1),
  email: z.string().min(1),
});

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: `0 ${theme.spacing(4)}`,
  },
}));

const Patient = () => {
  const { id } = useParams();
  const patientId = Number(id);
  const setPatient = useAppStore.use.setPatient();
  /***
   * TODO: move this state inside consultations tab
   */
  const [showConsultation, setShowConsultation] = useState<ShowConsultation | null>(null);

  const endpointsString = [
    PatientDetailsIncludeEnum.BasicInfo,
    PatientDetailsIncludeEnum.MedicalInfo,
    PatientDetailsIncludeEnum.RedFlagInfo,
    PatientDetailsIncludeEnum.ContactDetails,
    PatientDetailsIncludeEnum.CareInfo,
  ].join(',');

  const {
    result: patientDetailsResult,
    isError: isPatientDetailsError,
    isLoading: isPatientDetailsLoading,
    refetch: refetchPatientDetailsData,
  } = useGetPatientDetails(patientId, endpointsString);
  const patientDetailsData = patientDetailsResult.data?.body?.data;

  const previousDoctor = patientDetailsData?.careInfo?.attributes?.previousDoctor;
  useEffect(() => {
    /**
     * Close show consultation when a new patient is loaded via useNavigate
     * https://montugroup.atlassian.net/browse/B2C-8964
     */
    setShowConsultation(null);
  }, [patientId]);

  /**
   * hydrate the patient slice in store
   */
  useEffect(() => {
    setPatient({
      patientId: patientId,
      patientCode: patientDetailsData?.basicInfo?.attributes.patientCode ?? null,
      needsIhiVerification: false,
      basicInfo: {
        firstName: patientDetailsData?.basicInfo?.attributes.firstName,
        lastName: patientDetailsData?.basicInfo?.attributes.lastName,
        dob: patientDetailsData?.basicInfo?.attributes.dob,
        gender: patientDetailsData?.basicInfo?.attributes.gender,
        stateCode: patientDetailsData?.basicInfo?.attributes.residentialAddress?.state?.name,
      },
      redFlagInfo: {
        hasRedFlag: patientDetailsData?.redFlagInfo?.attributes.hasRedFlag,
        redFlagComment: patientDetailsData?.redFlagInfo?.attributes.redFlagComment,
      },
      contactDetails: {
        email: patientDetailsData?.contactDetails?.attributes.email,
        phone: patientDetailsData?.contactDetails?.attributes.phone,
      },
    });
  }, [patientDetailsData, patientId, setPatient]);

  const {
    data: patientAssessmentData,
    isLoading: isPatientAssessmentsLoading,
    refetch: refetchPatientAssessments,
  } = useGetPatientAssessments(patientId);

  const { data, setData } = usePatientDetailsData({
    patientDetailsData,
    patientAssessmentData,
  });

  const {
    patientConsultationsData,
    consultationsResponseCursors,
    handlePaginationChange,
    isFetchingPatientConsultations,
    refetchConsultations,
    setConsultationsCursors,
    setConsultationsSearch,
    onSort,
    isPatientConsultationsError,
  } = usePatientConsultations(patientId);

  const {
    data: concessionCardData,
    isLoading: concessionCardLoading,
    refetch: refetchPatientConcessionCard,
  } = useGetPatientConcessionCard(patientId);

  const { contents, setContents } = usePatientDetailsContent({
    patientId,
    patientDetailsData,
    patientConsultationsData,
    concessionCardData,
  });

  const prescriptionAssistantToolDetails = usePrescriptionAssistantToolDetails({
    patientDetails: contents,
  });

  if (isPatientDetailsError) {
    // @ts-expect-error -- this is a fix for showing the correct Error page
    const error = new Error(patientDetailsResult?.error?.body?.message || 'Get Patient Details Result Error');
    // @ts-expect-error -- this is a fix for showing the correct Error page
    error.status = patientDetailsResult?.error?.status; // ErrorBoundary defaults to "Something went wrong" without status
    throw error;
  }

  // Hooks to create/check/deactivate Patient Dass - (Depression, Anxiety, and Stress Scales)
  const { data: dassAssessment, refetch: refetchDassAssessment } = useDassAssessment({ patientId });
  const { data: dassAssessmentDetails, refetch: refetchDassAssessmentDetails } = useDassAssessmentDetails({
    patientId,
    dassAssessmentUuid: dassAssessment?.uuid ?? '',
  });
  const {
    data: dassAssessmentScoreDetail,
    refetch: refetchDassAssessmentScores,
    isLoading: isDassScoreDetailLoading,
  } = useDassAssessmentScores({
    patientId,
    enabled: !dassAssessment || dassAssessment.is_submitted,
  });
  const refetchDass = async () => {
    await Promise.all([refetchDassAssessment(), refetchDassAssessmentDetails(), refetchDassAssessmentScores()]);
  };

  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const { isAdmin, isNurse, isDoc, user, isSuperAdmin, isAdminOnly, isManager, isPtxSupportPartner } = getAuthData();
  const role = getRole();
  const [consultationStarted, setConsultationStarted] = useState<boolean>(false);
  const [consultationSubStatus, setConsultationSubStatus] = useState<null | ConsultationSubStatusType>(null);
  const { flags } = useFeatureFlags();
  const showPatientStudyAlert = flags[FF_SHOW_PATIENT_STUDY_ALERT];
  const isPrescriptionTabRedesignEnabled = flags[FF_PRESCRIPTION_TAB_REDESIGN];
  const isGDCEnabled = flags[FF_GDC_RELEASE_1];
  const isSkeletonEnabled = flags[FF_PATIENT_DETAILS_SKELETON_ENABLED];
  const ffAutosaveTour = flags[FF_PATIENT_PAGE_AUTOSAVE_TOUR];

  const [isSaving] = React.useState(false);

  // toast nofication for what's new in PMS
  useFeatureNotification({
    title: (
      <>
        What&apos;s new in <strong>PMS</strong>
      </>
    ),
    message: 'Autosave!',
    steps: ffAutosaveTour ? patientProfileAutoSave : [],
    onStartTour: () => setActiveTabIndex(0),
  });

  const handleChangeConcessionCard = () => {
    setContents({
      ...contents,
      patient: {
        ...contents.patient,
        // Safe to assume true as the form cannot be submitted with invalid details
        hasValidConcessionCard: true,
      },
    });
    refetchPatientConcessionCard();
  };

  const isConcessionValid = concessionCardData?.hasValidConcessionCard;
  const [isLoading, setIsLoading] = useState(true);

  const [expanded, setExpanded] = useState({
    dass_completion_panel: true,
  });

  const [medicalConditionDisabled, setMedicalConditionDisabled] = useState(false);

  let value;

  const SearchableSelectFields = ['state', 'trustedPharmacy', 'pmsDoctor'];
  const checkboxFields = [
    'medicalSummary',
    'PaymentVerified',
    'detailsConfirmed',
    'HomeDelivery',
    'consent',
    'Lactating',
    'falls',
    'SubstanceAbuse',
    'DrugDrug',
    'PsychosisHx',
    'MCprocess',
    'RecentlyIncarcerated',
    'SiTosh',
  ];

  const [dischargeModalOpen, setDischargeModal] = React.useState(false);

  const arePatientFormsEditable = data.isActive;

  const handleDischargeModalClose = (status: boolean) => {
    setDischargeModal(status);
    refreshAllPatientData();
  };

  // @ts-expect-error -- this function needs to be refactored. it's based on legacy implementation. can be moved inside ConsultationTab too.
  const handleDataInput = (prop) => (event) => {
    if (prop === 'DoB') {
      if (event) {
        const formattedDate = new Date(Date.UTC(event.year(), event.month(), event.date()));
        value = formattedDate.toISOString();
      } else {
        value = null;
      }
    } else if (checkboxFields.indexOf(prop) > -1) {
      value = event.target.checked;
    } else if (SearchableSelectFields.indexOf(prop) > -1) {
      value = event ? event.value : '';
    } else {
      value = event.target.value;
    }

    setData({
      ...data,
      [prop]: value,
    });
  };

  const { data: doctorsList = [] } = useDoctors();

  const { updatePatientAssessment } = useUpdatePatientAssessment();

  const refreshAllPatientData = async () => {
    await refetchPatientDetailsData();
  };
  const refreshConsultationTab = async () => {
    await Promise.all([[refetchConsultations(), refetchPatientDetailsData(), refetchPatientAssessments()]]);
  };

  // Enables/Disables Medical Condition Fields
  useEffect(() => {
    const medicalInfo = patientDetailsData?.medicalInfo?.attributes;
    const medicalCondition1 = medicalInfo?.medicalCondition1 ? JSON.parse(medicalInfo.medicalCondition1) : [];

    setMedicalConditionDisabled(
      isNurse ||
        isAdmin ||
        isSuperAdmin ||
        (medicalCondition1.length === 0 &&
          !medicalInfo?.medicalCondition2 &&
          !medicalInfo?.medicalCondition3 &&
          !medicalInfo?.medicalCondition4)
        ? false
        : true,
    );
  }, [isAdmin, isNurse, isSuperAdmin, patientDetailsData?.medicalInfo?.attributes]);

  const scheduledConsultActive = useIsScheduledConsultActive();

  const tabs = buildPatientTabConfig({ isGDCEnabled, scheduledConsultActive });

  const { activeTabIndex, setActiveTabIndex, setActiveTabById, getTabIndex } = useTabsState<PatientTabId>({ tabs });

  const handleActiveTabChange = (_event: unknown, newValue: React.SetStateAction<number>) => {
    setActiveTabIndex(newValue);
  };

  const activatePrescriptionsTab = () => {
    setActiveTabById(PATIENT_TAB_IDS.PRESCRIPTIONS);
  };

  const checkRequiredFieldSchema = async () => {
    const validateFields = CheckRequiredFieldSchema.safeParse({
      isActive: data.isActive,
      street_line1: data.street.line1,
      suburb: data.suburb,
      postcode: data.postcode,
      state: data.state,
      email: data.email,
    });

    if (!validateFields.success) {
      const error = validateFields.error.issues.map((i) => i.path.join('.')).join(', ');
      toast.error(`Unable to save the patient details, please fill in the required fields - ${error}`);
      return false;
    }

    return true;
  };

  const onSaveConsultation = async ({
    saveConsult = true,
    closeConsultation = false,
    skipAssessment = true,
    askConfirmation = false,
  }: OnSaveConsultationOptions = {}) => {
    if (!data.isActive) {
      return;
    }

    if (!saveConsult || isAdminOnly || isPtxSupportPartner) {
      return;
    }

    const CANCELLED_STATUS_ID = 4;
    const NO_SHOW_STATUS_ID = 5;

    if (
      showConsultation?.status_id !== NO_SHOW_STATUS_ID &&
      showConsultation?.status_id !== CANCELLED_STATUS_ID &&
      !(await checkRequiredFieldSchema())
    ) {
      return;
    }

    if (!skipAssessment) {
      const updatePatientAssessmentRequestBody: UpdatePatientAssessmentBodyDto = {
        id: Number(patientId),
        type: 'patient-assessment' as const,
        attributes: {
          patientDetailsConfirmed: data.detailsConfirmed,
          medicalSummaryVerified: data.medicalSummary,
          paymentVerified: data.PaymentVerified,
          psychosis: data.psycosis ?? undefined,
          drugDrug: data.DrugDrugComment ?? undefined,
          falls: data.fallsComment ?? undefined,
          pregnantLactating: data.LactatingComment ?? undefined,
          substanceAbuse: data.SubstanceAbuseComment ?? undefined,
          recentlyIncarcerated: data.RecentlyIncarceratedComment ?? undefined,
          siTosh: data.SiToshComment ?? undefined,
          mcProcessExplained: data.MCprocess,
          treatmentGoal: data.treatmentGoal ?? undefined,
          hasPsychosis: data.PsychosisHx,
          hasDrugDrug: data.DrugDrug,
          hasSubstanceAbuse: data.SubstanceAbuse,
          hasFalls: data.falls,
          hasPregnantLactating: data.Lactating,
          hasRecentlyIncarcerated: data.RecentlyIncarcerated,
          hasSiTosh: data.SiTosh,
          notes: data.notes ?? undefined,
        },
      };

      const updatePatientAssessmentResult = await savePatientAssessment(updatePatientAssessmentRequestBody);

      if (updatePatientAssessmentResult?.status !== HttpStatus.OK) {
        toast.error('Failed to update patient assessment.');
        return;
      }
      toast.success('Patient Details Updated.');
    }

    await saveConsultation(null, {
      askConfirmation,
      refetchData: false,
      closeConsultation,
    });
    await refreshAllPatientData();
  };

  const savePatientAssessment = async (body: UpdatePatientAssessmentBodyDto) => {
    try {
      return await updatePatientAssessment(body);
    } catch (err) {
      toast.error({
        title: 'Failed to update patient assessment.',
        // @ts-expect-error -- to be refactored
        message: err?.body?.error?.message,
      });
    }
  };

  const shouldUpdateConsultationStatus = useMemo(() => {
    return (
      contents.consultations.find((consultation: Consultation) => consultation.id === showConsultation?.id)
        ?.status_id === showConsultation?.status_id
    );
  }, [contents, showConsultation]);

  const onSaveConsultationComplete = (confirmed: boolean) => {
    if (confirmed) {
      setShowConsultation(null);
      setConsultationSubStatus(null);
    }
  };

  const { saveConsultation, submitNurseConsultation } = useSaveConsultation({
    consultation: showConsultation,
    shouldUpdateConsultationStatus,
    onRefresh: refreshAllPatientData,
    onSaveConsultation: onSaveConsultation,
    onSaveComplete: onSaveConsultationComplete,
  });

  const handleChange = (panel: string) => (_event: unknown, isExpanded: boolean) => {
    setExpanded({ ...expanded, [panel]: isExpanded });
  };

  // Manually update state from this data to prevent a full network request/refresh, this also prevents wiping dirty
  // fields like notes.
  function handleCreditCardHasBeenCharged(consultationId: number) {
    setContents({
      ...contents,
      consultations: contents.consultations.map((consult) => {
        if (consult.id === consultationId) {
          return {
            ...consult,
            payment_queued: true,
          };
        }
        return consult;
      }),
    });
    setShowConsultation((prevShowConsultation: ShowConsultation | null) =>
      prevShowConsultation
        ? {
            ...prevShowConsultation,
            payment_queued: true,
          }
        : null,
    );
  }

  // Manually update state from this data to prevent a full network request/refresh, this also prevents wiping dirty
  // fields like notes.
  function handleInvoiceHasBeenCreated(consultationId: number, invoiceCode?: string) {
    setContents({
      ...contents,
      consultations: contents.consultations.map((consult) => {
        if (consult.id === consultationId) {
          return {
            ...consult,
            xero_consultation_code: invoiceCode,
          };
        }
        return consult;
      }),
    });
    setShowConsultation((prevShowConsultation: ShowConsultation | null) =>
      prevShowConsultation
        ? {
            ...prevShowConsultation,
            xero_consultation_code: invoiceCode ?? '',
          }
        : null,
    );
  }

  const handleMedicalConditionSubmit = (
    medicalConditions: Pick<
      PatientDetailsDataState,
      | 'medical_condition_1'
      | 'medical_condition_2'
      | 'medical_condition_3'
      | 'medical_condition_4'
      | 'others_1_comment'
      | 'others_2_comment'
    >,
  ) => {
    setData((current) => ({ ...current, ...medicalConditions }));
  };

  const handleMedicalSummarySubmit = (medicalSummary: MedicalSummaryFields) => {
    setData((current) => ({
      ...current,
      presentingComplaint: medicalSummary.conditions ?? null,
      symptoms: medicalSummary.symptoms ?? null,
      trialledMedication: medicalSummary.medications ?? null ?? null,
      currentMedicationList: medicalSummary.currentMedications ?? null,
      medicalHistory: medicalSummary.comments ?? null,
      drinksPerWeek: medicalSummary.alcoholPerWeek ?? null,
      smokingEx: medicalSummary.smoking ?? null,
    }));
  };

  useEffect(() => {
    setIsLoading(isPatientDetailsLoading || isPatientAssessmentsLoading || concessionCardLoading);
  }, [isPatientDetailsLoading, isPatientAssessmentsLoading, concessionCardLoading, setIsLoading]);

  if (isLoading && !isSkeletonEnabled) {
    return (
      <Box justifyContent="center" alignItems="center" gap="1" display="flex" height="100vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <PatientContext.Provider
      value={{
        patientId: patientId,
        email: patientDetailsData?.contactDetails?.attributes.email,
        FirstName: patientDetailsData?.basicInfo?.attributes.firstName,
        LastName: patientDetailsData?.basicInfo?.attributes.lastName,
        phone: patientDetailsData?.contactDetails?.attributes.phone,
        redFlag: patientDetailsData?.redFlagInfo?.attributes.hasRedFlag,
        redFlagComment: patientDetailsData?.redFlagInfo?.attributes.redFlagComment,
      }}
    >
      <BrandThemeProvider theme={alternaleaf}>
        <Grid
          container
          paddingX={{
            sm: 1,
            md: 3,
          }}
          columnSpacing={{
            xs: 0,
            sm: 4,
            md: 7,
            lg: 7,
            xl: 8,
          }}
        >
          {/* Patient left hand panel */}
          <Grid
            xs={12}
            sm={6}
            lg={4}
            item
            sx={{
              boxShadow: { sm: `0px 4px 10px 0px rgba(48, 4, 49, 0.15)` },
            }}
            data-snowplow-tracking-group="patient-details"
          >
            <Box>
              <Box sx={{ mx: 4 }}>
                {/* "Header" */}
                <Box>
                  {/** Actions: Discharge patient & Save Changes */}
                  <PatientDischargeAndSaveStatus
                    data={data}
                    patientId={patientId}
                    user={user}
                    isNurse={isNurse}
                    isSuperAdmin={isSuperAdmin}
                    isManager={isManager}
                    isAdminOnly={isAdminOnly}
                    dischargeModalOpen={dischargeModalOpen}
                    setDischargeModal={setDischargeModal}
                    handleDischargeModalClose={handleDischargeModalClose}
                    isSaving={isSaving}
                    isLoading={isLoading}
                  />

                  {/** Patient Names and basic info */}
                  <PatientBasicInfo isLoading={isPatientDetailsLoading} />

                  {/** Studies */}
                  {showPatientStudyAlert && <PatientStudiesAlert patientId={patientId} />}
                </Box>
                {/** Concession Status */}

                <Box>
                  <ManageConcession
                    patientId={patientId}
                    ready={!concessionCardLoading}
                    // @ts-expect-error -- leave ts issues for now.
                    // too risky to change at this point since this is legacy and this component is used both in V2 and V3.
                    // revisit once V3 is fully released and by that time, we can get rid of V2 completely
                    concessionCard={concessionCardData}
                    onChangeConcessionCard={handleChangeConcessionCard}
                    isConcessionCardValid={isConcessionValid}
                    editable={arePatientFormsEditable}
                  />
                </Box>
                <Box>
                  <Divider sx={{ my: 4 }} />
                  {!isAdminOnly && (
                    <MedicalInformation
                      editable={arePatientFormsEditable}
                      defaultValues={{
                        cannabisNaive: data.cannabisNaive,
                        hasAllergy: Boolean(data.is_allergy),
                        hasCardioDisease: Boolean(data.is_cardio_disease),
                        allergies: data.allergies,
                        cardioDisease: data.cardioDisease,
                      }}
                      onSubmit={({ hasAllergy, hasCardioDisease, ...medicalSummary }) => {
                        setData((current) => ({
                          ...current,
                          ...medicalSummary,
                          is_allergy: hasAllergy,
                          is_cardio_disease: hasCardioDisease,
                        }));
                      }}
                      isLoading={isLoading}
                    />
                  )}
                  {isAdminOnly && (
                    <Accordion
                      expanded={expanded['dass_completion_panel']}
                      onChange={handleChange('dass_completion_panel')}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel2bh-content"
                        id="panel2bh-header"
                      >
                        <Box mt={3} p={0.5} component="h2">
                          DASS Completion
                        </Box>
                      </AccordionSummary>
                      <Box padding={3}>
                        {dassAssessmentDetails.is_submitted ? (
                          <Chip
                            color="success"
                            label={`DASS Completed ${dassAssessmentDetails.submitted_date && DateTime.fromISO(dassAssessmentDetails.submitted_date).toFormat('dd-MM-yyyy')}`}
                          />
                        ) : (
                          <Chip color="warning" label="DASS Not Completed" />
                        )}
                      </Box>
                    </Accordion>
                  )}

                  {!isAdminOnly && (
                    <>
                      <MedicalCondition
                        editable={!medicalConditionDisabled && arePatientFormsEditable}
                        defaultValues={{
                          medicalCondition1: data.medical_condition_1,
                          medicalCondition2: data.medical_condition_2,
                          medicalCondition3: data.medical_condition_3,
                          medicalCondition4: data.medical_condition_4,
                          othersComment1: data.others_1_comment,
                          othersComment2: data.others_2_comment,
                        }}
                        userEmail={user?.email}
                        dassAssessmentDetails={dassAssessmentDetails}
                        dassAssessmentScoreDetail={dassAssessmentScoreDetail}
                        isDassScoreDetailLoading={isDassScoreDetailLoading}
                        fetchDassAssessment={refetchDass}
                        onSubmit={(medicalCondition) => handleMedicalConditionSubmit(medicalCondition)}
                        isLoading={isLoading}
                      />
                      <MedicalSummary
                        editable={arePatientFormsEditable}
                        defaultValues={{
                          conditions: data.presentingComplaint,
                          symptoms: data.symptoms,
                          medications: data.trialledMedication,
                          currentMedications: data.currentMedicationList,
                          comments: data.medicalHistory,
                          alcoholPerWeek: data.drinksPerWeek ?? 0,
                          smoking: data.smokingEx,
                        }}
                        onSubmit={(medicalSummary) => handleMedicalSummarySubmit(medicalSummary)}
                        isLoading={isLoading}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
          {/* END Patient medical condition and summary */}
          {/* Tab Panel column  */}
          <Grid item xs={12} sm={6} lg={8}>
            <Box py={5}>
              <Box>
                <Tabs
                  value={activeTabIndex}
                  onChange={handleActiveTabChange}
                  centered
                  variant={isSmallerScreen ? 'scrollable' : 'fullWidth'}
                  scrollButtons={false}
                  sx={{
                    mx: { xs: 4, sm: 0 },
                    ['.MuiTab-root']: {
                      fontSize: (theme) => theme.spacing(4),
                    },
                  }}
                >
                  {tabs.map((tab) => tab.visible && <Tab label={tab.label} key={tab.id} data-dd-privacy="allow" />)}
                </Tabs>
              </Box>
              <StyledTabPanel value={activeTabIndex} index={getTabIndex(PATIENT_TAB_IDS.SCHEDULED_CONSULT)}>
                <ScheduledConsultAnalyticsContext>
                  <ScheduledConsult
                    prescriptionDetails={prescriptionAssistantToolDetails}
                    isConcessionPatient={concessionCardData?.hasValidConcessionCard ?? false}
                    isPatientActive={patientDetailsData?.basicInfo?.attributes.userActive ?? false}
                    consultationStarted={consultationStarted}
                    setConsultationStarted={setConsultationStarted}
                    onPrescriptionErrorClosed={activatePrescriptionsTab}
                  />
                </ScheduledConsultAnalyticsContext>
              </StyledTabPanel>
              <StyledTabPanel
                value={activeTabIndex}
                index={getTabIndex(PATIENT_TAB_IDS.DETAILS)}
                data-snowplow-tracking-group="patient-details"
              >
                <PatientDetailsTab
                  setData={setData}
                  setContents={setContents}
                  data={data}
                  patientDetailsData={patientDetailsData}
                  contents={contents}
                  role={role}
                  arePatientFormsEditable={arePatientFormsEditable}
                  isLoading={isLoading && isPatientDetailsLoading}
                  concessionCardData={concessionCardData}
                />
              </StyledTabPanel>
              <StyledTabPanel value={activeTabIndex} index={getTabIndex(PATIENT_TAB_IDS.CONSULTATIONS)}>
                <ConsultationTabAnalyticsContext>
                  <ConsultationTab
                    onCreditCardHasBeenCharged={handleCreditCardHasBeenCharged}
                    onInvoiceHasBeenCreated={handleInvoiceHasBeenCreated}
                    data={{
                      consultations: patientConsultationsData,
                    }}
                    responseCursors={consultationsResponseCursors}
                    fields={data}
                    doctor={contents.doctor}
                    doctorsList={doctorsList}
                    handleChange={handleDataInput}
                    handlePaginationChange={handlePaginationChange}
                    patientId={patientId}
                    onSaveConsultation={onSaveConsultation}
                    patient={contents.patient}
                    showConsultation={showConsultation}
                    setShowConsultation={setShowConsultation}
                    consultationStarted={consultationStarted}
                    setConsultationStarted={setConsultationStarted}
                    consultationSubStatus={consultationSubStatus}
                    setConsultationSubStatus={setConsultationSubStatus}
                    saveConsultation={saveConsultation}
                    submitNurseConsultation={submitNurseConsultation}
                    refresh={refreshConsultationTab}
                    isLoadingConsultations={isFetchingPatientConsultations}
                    setConsultationsCursors={setConsultationsCursors}
                    setConsultationsSearch={setConsultationsSearch}
                    onSort={onSort}
                    isErrorConsultations={isPatientConsultationsError}
                    onPrescriptionErrorClosed={activatePrescriptionsTab}
                    consultationSummary={patientDetailsData?.basicInfo?.attributes?.consultationSummary}
                    prescriptionDetails={prescriptionAssistantToolDetails}
                  />
                </ConsultationTabAnalyticsContext>
              </StyledTabPanel>

              <StyledTabPanel value={activeTabIndex} index={getTabIndex(PATIENT_TAB_IDS.PRESCRIPTIONS)}>
                <PrescriptionTabAnalyticsContext>
                  {isPrescriptionTabRedesignEnabled ? (
                    <PrescriptionTab
                      isEditable={arePatientFormsEditable}
                      prescriptionDetails={prescriptionAssistantToolDetails}
                    />
                  ) : (
                    <PrescriptionTabLegacy
                      patientActive={data.isActive}
                      doctor={contents.doctor}
                      patient={contents.patient}
                      patientId={id ?? ''}
                      isLoading={isLoading}
                    />
                  )}
                </PrescriptionTabAnalyticsContext>
              </StyledTabPanel>
              <StyledTabPanel value={activeTabIndex} index={getTabIndex(PATIENT_TAB_IDS.DOCUMENTATION)}>
                <DocumentsTab patientId={patientId} disabled={!data.isActive} />
              </StyledTabPanel>
            </Box>
          </Grid>
          {/* Tab Panel column  */}
        </Grid>
        <AcknowledgementModal
          doctorStatus={previousDoctor?.hasTransferred ?? false}
          doctorName={previousDoctor?.hasTransferred ? previousDoctor.name ?? '' : ''}
          patientId={patientId}
          isDoc={isDoc}
        />
        {isGDCEnabled && <UnsavedScheduledConsultPrompt doctorId={contents.doctor.id} />}
      </BrandThemeProvider>
    </PatientContext.Provider>
  );
};

export default withAmazonConnectCcp(Patient);
